// -----------------------------------------------------------------------------
// UNSAVED-CHANGES GUARD (v0.4.3, Kyle 11/Oct/26: "any unsaved should be prompted")
//
// Any form with unsaved edits registers itself:   useLeaveGuard(dirty, save)
// Before the app leaves the page, it asks:         Save / Discard / Keep editing
//   - every page change goes through the router (core/router.ts → useRoute): sidebar links, project switcher,
//     breadcrumbs, the browser's Back / Forward buttons, Esc / Backspace "go back", navigate() calls;
//   - in-page switches (e.g. picking another person in Members while the panel has edits) call
//     requestLeave(() => …) themselves;
//   - closing or reloading the tab gets the browser's own "Leave site?" box (beforeunload; its look is fixed).
// Pop-ups ask on ×, Esc, Cancel and outside click with their own ConfirmSave (core/ui/kit.tsx).
//
//   dirty  — true while there is something unsaved. Pass false the moment a save succeeds (before navigating).
//   save   — optional; called by "Save" in the question. Return true when saved, false to stay (e.g. a check failed
//            and the form now shows why). Without it the question offers Discard / Keep editing only.
// Pure parts (registry, decision) are unit-tested in tests/unit/leaveGuard.test.ts.
// -----------------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";

export interface Guard { dirty: () => boolean; save?: () => boolean | Promise<boolean> }
const guards = new Set<Guard>();

export function registerGuard(g: Guard, set: Set<Guard> = guards): () => void {
  set.add(g);
  return () => { set.delete(g); };
}
/** Guards that currently hold unsaved edits. */
export function dirtyGuards(set: Set<Guard> = guards): Guard[] { return [...set].filter((g) => g.dirty()); }
export const anyDirty = (set: Set<Guard> = guards) => dirtyGuards(set).length > 0;

/** Register while mounted. `dirty` and `save` are read fresh each time (latest render).
 *  Returns release(): call it the moment a save succeeds when the app navigates straight after (e.g. Add issue opens
 *  the new issue) — React redraws a moment later, so without it the guard would still see the old "unsaved" state. */
export function useLeaveGuard(dirty: boolean, save?: () => boolean | Promise<boolean>): () => void {
  const ref = useRef({ dirty, save });
  ref.current = { dirty, save };
  useEffect(() => registerGuard({ dirty: () => ref.current.dirty, save: () => (ref.current.save ? ref.current.save() : false) }), []);
  return () => { ref.current = { ...ref.current, dirty: false }; };
}

// ---- the question (one at a time) ----
export interface Ask { canSave: boolean; proceed: () => void; stay: () => void }
let asking: Ask | null = null;
const listeners = new Set<(a: Ask | null) => void>();
const emit = () => listeners.forEach((l) => l(asking));

/** Run `proceed` now if nothing is unsaved; otherwise ask first. Returns true when it ran at once. */
export function requestLeave(proceed: () => void, stay: () => void = () => undefined): boolean {
  const dirty = dirtyGuards();
  if (dirty.length === 0) { proceed(); return true; }
  asking = { canSave: dirty.every((g) => !!g.save), proceed, stay };
  emit();
  return false;
}

/** Answers from the question pop-up (GuardHost in core/ui/kit.tsx). */
export async function answer(choice: "save" | "discard" | "keep"): Promise<void> {
  const a = asking;
  if (!a) return;
  if (choice === "keep") { asking = null; emit(); a.stay(); return; }
  if (choice === "save") {
    for (const g of dirtyGuards()) {
      const ok = await g.save?.();
      if (!ok) { asking = null; emit(); a.stay(); return; }     // a check failed: stay, the form shows why
    }
  }
  bypass = true;                                                 // Discard, or every save succeeded
  asking = null; emit();
  try { a.proceed(); } finally { setTimeout(() => { bypass = false; }, 100); }
}

/** For the router: lets the navigation that follows an answer through without asking again. */
let bypass = false;
export function bypassing(): boolean { const b = bypass; bypass = false; return b; }

export function useAsk(): Ask | null {
  const [a, setA] = useState<Ask | null>(asking);
  useEffect(() => { listeners.add(setA); return () => { listeners.delete(setA); }; }, []);
  return a;
}

// Tab close / reload: the browser's own box.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => { if (anyDirty()) { e.preventDefault(); e.returnValue = ""; } });
}
